import styled from '@emotion/styled'
import { CreateFloorRule, FloorRule, UpdateFloorRule } from '@pubstack/common/src/adm/rules'
import { VALIDATE_ADUNIT_SIZE_REGEX } from '@pubstack/common/src/adunit'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { floatRegExp } from '@pubstack/common/src/input'
import { FunctionComponent, useEffect, useState } from 'react'
import { SubmitHandler, useFieldArray, useForm, useWatch, Validate } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { ChipListInput } from '~/components/ChipListInput'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { BaseInputElement, Input } from '~/components/Input'
import { Link } from '~/components/Link'
import { SelectOptionProp } from '~/components/SelectableOptionsPopover'
import { useToast } from '~/components/Toast/useToasts'
import { ToggleInput } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { PureAdmEditPage } from '~/modules/adm/PureAdmEditPage'
import { WithClassName } from '~/types/utils'
import { SUPPORT_MAIL } from '~/utils/constants'

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0;
  max-width: 1000px;
  ${Fonts.P1}
  h2 {
    ${Fonts.H2}
    font-weight: 500;
  }

  ${BaseInputElement} {
    width: 402px;
    padding: 20px 0;
  }

  & > ${BaseInputElement} {
    margin-bottom: 12px;
  }
`

const Subtitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`

const FormButtonsWrapper = styled.div`
  text-align: right;
  padding: 20px;
  ${Button} {
    margin-left: 15px;
  }
`

const RuleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const FloorPriceWrapper = styled(RuleWrapper)`
  ${BaseInputElement} {
      width: 141px;
      padding: 20px 0;
    }
`

const DynamicFloorWrapper = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 20px;
  margin-bottom: 24px;
  p:first-of-type {
    ${Fonts.P1};
  }
  `

const CustomLabel = styled.label`
  width: 70px;
  text-align: right;
`

const CustomFieldset = styled.fieldset`
  padding: 0;
  margin-bottom: 16px;
  border: 1px solid ${Colors.Platinum};
  border-radius: 3px;
`
const AddSetting = styled(Button)`
  width: fit-content;
`

const DeleteButton = styled(Button)`
  display: inline-flex;
  border-radius: 50%;
  justify-content: center;
  line-height: 1;
  width: 38px;
  height: 38px;
  padding: 0;
`
type PureFloorPriceEditPageProps = WithClassName & {
  floorRule?: FloorRule
  onBack: () => void
  breadcrumbs: React.ReactNode
  onCreateFloorRule: (floorRule: CreateFloorRule) => Promise<void>
  onUpdateFloorRule: (floorRule: UpdateFloorRule, stacksUse: boolean) => Promise<void>
  existingFloorRules: FloorRule[]
  currencySymbol: CurrencySymbol
  isEditing: boolean
  isLoading: boolean
  isError: boolean
  isPbs: boolean
  /** Only an owner writes a rule; the form stays readable for everyone else. */
  canSave?: boolean
  saveDisabledReason?: string
}

export const parseFloorPrice = (floor: string) => Number.parseFloat(floor.replace(',', '.'))

const mediaTypesOptions: SelectOptionProp[] = [
  { label: 'Banner', value: 'banner', trackingLabel: 'adm-floor-rule-banner-set' },
  { label: 'Native', value: 'native', trackingLabel: 'adm-floor-rule-native-set' },
  { label: 'Video', value: 'video', trackingLabel: 'adm-floor-rule-video-set' },
]

const getDuplicatedSettings = (settings: FloorRule['settings']): number[] => {
  if (!settings?.length) return []
  const settingsMap = new Map<string, number>()
  const duplicates = new Set<number>()

  for (let i = 0; i < settings.length; i++) {
    const setting = settings[i]
    if (!setting.sizes?.length || !setting.mediaTypes?.length) continue

    for (const mediaType of setting.mediaTypes) {
      for (const size of setting.sizes) {
        const key = `${mediaType}:${size}`

        if (settingsMap.has(key)) {
          duplicates.add(settingsMap.get(key)!)
          duplicates.add(i)
        } else {
          settingsMap.set(key, i)
        }
      }
    }
  }
  return Array.from(duplicates)
}

const UnstyledPureFloorPriceEditPage: FunctionComponent<PureFloorPriceEditPageProps> = ({
  className,
  floorRule,
  onBack,
  onCreateFloorRule,
  breadcrumbs,
  existingFloorRules,
  currencySymbol,
  isEditing,
  onUpdateFloorRule,
  isLoading,
  isError,
  isPbs,
  canSave = true,
  saveDisabledReason,
}) => {
  const toast = useToast()

  const defaultValues: CreateFloorRule | { floor: number } = floorRule
    ? {
        ...floorRule,
        floor: floorRule.floor / 100,
        settings: floorRule.settings?.map((setting) => ({ ...setting, floor: setting.floor / 100 })),
      }
    : {
        floor: 0,
        dynamic: true,
      }
  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset,
  } = useForm<FloorRule>({ mode: 'onChange', defaultValues })

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'settings',
  })
  useEffect(() => {
    reset(
      floorRule
        ? {
            ...floorRule,
            floor: floorRule.floor / 100,
            settings: floorRule.settings?.map((setting) => ({ ...setting, floor: setting.floor / 100 })),
          }
        : undefined
    )
  }, [floorRule])

  const [duplicatedSettings, setDuplicatedSettings] = useState<number[] | undefined>(undefined)

  const settings = useWatch({ control, name: 'settings' })

  const validateSizes: Validate<string[] | undefined, FloorRule> = (sizesToValidate) => {
    if (!sizesToValidate) return true
    if (!sizesToValidate.every((size) => VALIDATE_ADUNIT_SIZE_REGEX.test(size))) {
      return 'Sizes should follow this format : [Num]x[Num] (ex: 300x250)'
    }
    return true
  }
  const onSubmit: SubmitHandler<FloorRule> = (newRule) => {
    if (newRule.settings && newRule.settings.length > 1) {
      const duplicates = getDuplicatedSettings(newRule.settings)
      setDuplicatedSettings(duplicates)
      if (duplicates.length) {
        return toast.alert(`Multiple floors are defined for the same mediatype/size combination`)
      }
    }
    const toEdit = {
      ...newRule,
      floor: Math.round(parseFloorPrice(newRule.floor.toString()) * 100),
      settings: newRule.settings?.map((setting) => ({ ...setting, floor: Math.round(parseFloorPrice(setting.floor.toString()) * 100) })),
    }
    isEditing ? onUpdateFloorRule(toEdit, !!toEdit.stacksUse) : onCreateFloorRule(toEdit)
  }

  return (
    <PureAdmEditPage title={isEditing ? `Edit: ${floorRule?.name ?? ''}` : 'New floor price rule'} breadcrumbs={breadcrumbs} color={Colors.Forest} onBackClick={onBack}>
      {isError && (
        <IllustrationMessage
          iconName={'sync_problem'}
          iconSize={'96px'}
          title={'Something went wrong.'}
          message={
            <>
              We could not load this floor price rule. Please refresh the page or contact{' '}
              <Link label={SUPPORT_MAIL} href={`mailto:${SUPPORT_MAIL}`} trackingLabel={'common-mail-to-support-navigate'} /> if the issue persists.
            </>
          }
        />
      )}
      {!isLoading && !isError && (
        <form className={className} id={'newRuleForm'} onSubmit={handleSubmit(onSubmit)}>
          <FormWrapper>
            <h2>Identification</h2>
            <Input
              type={'text'}
              label={'Name'}
              control={control}
              name={'name'}
              error={errors.name?.message}
              rules={{
                required: { value: true, message: 'Name required.' },
                validate: {
                  value: (formValue) => {
                    return (formValue as string) !== floorRule?.name && existingFloorRules.map((f) => f.name).includes(formValue as string) ? 'A rule with this name already exists' : true
                  },
                },
              }}
              maxLength={100}
            />
            <Subtitle>
              <h2>Rule definition</h2>
              {isPbs
                ? 'Define a default floor price and/or floor prices for specific mediatypes.'
                : 'Define a default floor price and/or floor prices for specific mediatypes, sizes, or mediatypes x sizes.'}
            </Subtitle>
            <CustomFieldset>
              <FloorPriceWrapper>
                <CustomLabel>Default</CustomLabel>
                <Input
                  type={'text'}
                  control={control}
                  name={'floor'}
                  min={0}
                  error={errors.floor?.message}
                  rules={{
                    required: { value: true, message: 'Floor price required.' },
                    pattern: { value: floatRegExp, message: 'Incorrect value.' },
                  }}
                  textRight={currencySymbol}
                />
              </FloorPriceWrapper>
            </CustomFieldset>

            {fields?.map((item, index) => (
              <CustomFieldset key={item.id}>
                <RuleWrapper>
                  <CustomLabel>Target</CustomLabel>
                  <ChipListInput
                    chipColor={Colors.Topaze}
                    name={`settings.${index}.mediaTypes`}
                    selectableOptions={mediaTypesOptions}
                    label={'Media types'}
                    control={control}
                    displayLabel={true}
                    error={errors.settings?.[index]?.mediaTypes?.message}
                    rules={{
                      validate: {
                        required: (value) => (value?.length ?? 0) > 0 || 'At least one media type is required.',
                      },
                    }}
                  ></ChipListInput>
                  {!isPbs && (
                    <ChipListInput
                      chipColor={Colors.Topaze}
                      name={`settings.${index}.sizes`}
                      label={'Sizes (optional)'}
                      helper={'Leave Sizes empty to target all of them.'}
                      control={control}
                      error={errors.settings?.[index]?.sizes?.message}
                      isError={duplicatedSettings?.includes(index)}
                      rules={{
                        validate: validateSizes,
                      }}
                    ></ChipListInput>
                  )}
                  <>
                    <DeleteButton variant={'tertiary'} type={'button'} onClick={() => remove(index)} trackingLabel={'adm-floor-price-rules-rule-unset'}>
                      <Icon name={'delete'} height="26px" width="26px" />
                    </DeleteButton>
                  </>
                </RuleWrapper>
                <FloorPriceWrapper>
                  <CustomLabel>Price</CustomLabel>
                  <Input
                    type={'text'}
                    control={control}
                    name={`settings.${index}.floor`}
                    min={0}
                    error={errors.settings?.[index]?.floor?.message}
                    rules={{
                      required: { value: true, message: 'Floor price required.' },
                      pattern: { value: floatRegExp, message: 'Incorrect value.' },
                    }}
                    textRight={currencySymbol}
                  />
                </FloorPriceWrapper>
              </CustomFieldset>
            ))}
            <AddSetting
              variant={'secondary'}
              iconName={'plus'}
              onClick={() => {
                append({ mediaTypes: [], sizes: [], floor: 0 })
              }}
              disabled={!!settings && settings?.some((setting) => setting.mediaTypes.length === 0)}
              trackingLabel={'adm-floor-price-rules-add-rule-set'}
            >
              Add setting
            </AddSetting>
          </FormWrapper>
          {!isPbs && (
            <DynamicFloorWrapper>
              <ToggleInput trackingLabel={'adm-dynamic-floor-set'} id={'dynamic'} name={'dynamic'} control={control}></ToggleInput>
              <p>Dynamic GAM UPR (Unified Pricing Rules)</p>
            </DynamicFloorWrapper>
          )}
          <FormButtonsWrapper>
            <Button variant={'tertiary'} onClick={onBack} trackingLabel={'adm-floor-price-rules-cancel'}>
              Cancel
            </Button>
            <Tooltip title={saveDisabledReason} disabled={canSave}>
              <Button variant={'primary'} type={'submit'} disabled={!canSave || !isDirty} trackingLabel={'adm-floor-price-rules-save'}>
                Save rule
              </Button>
            </Tooltip>
          </FormButtonsWrapper>
        </form>
      )}
    </PureAdmEditPage>
  )
}

export const PureFloorPriceEditPage = styled(UnstyledPureFloorPriceEditPage)``
